/**
 * A casca da interface: porta de entrada, navegação e roteamento.
 *
 * Roteamento por hash (`#/contas`) de propósito. Rota de verdade exigiria o
 * servidor devolver o index em qualquer caminho, e aí `GET /posts` — que é
 * rota da API — passaria a ser ambíguo entre dado e página. O `#` deixa o
 * servidor fora disso.
 *
 * --------------------------------------------------------------------------
 * A PORTA PERGUNTA AO SERVIDOR, NÃO AO NAVEGADOR
 * --------------------------------------------------------------------------
 * A sessão vive num cookie `httpOnly`, que este código não consegue ler — e é
 * exatamente por isso que ele existe assim. Então a página não tem como saber
 * sozinha se há sessão: ela pergunta, com `GET /auth/me`.
 *
 * Uma requisição a mais na abertura, em troca de o segredo não ser legível
 * pelo JavaScript da página. Numa ferramenta cuja sessão publica na conta de
 * um cliente, é troca barata.
 */
import { Api, ApiError, type UsuarioLogado } from "./api.js";
import { el, limpar } from "./dom.js";
import {
  definirMarcas,
  escolherMarca,
  marcaAtual,
  todasAsMarcas
} from "./marca.js";
import { avisar, type Tela } from "./tela.js";
import { telaArtes } from "./telas/artes.js";
import { telaCalendario } from "./telas/calendario.js";
import { telaContas } from "./telas/contas.js";
import { telaMidia } from "./telas/midia.js";
import { telaNovaPeca } from "./telas/nova-peca.js";
import { telaSaude } from "./telas/saude.js";
import { telaUsuarios } from "./telas/usuarios.js";

const ROTAS: Record<string, () => Tela> = {
  "/calendario": telaCalendario,
  "/nova": telaNovaPeca,
  "/artes": telaArtes,
  "/midia": telaMidia,
  "/contas": telaContas,
  "/saude": telaSaude,
  "/usuarios": telaUsuarios
};

const MENU: [string, string][] = [
  ["/calendario", "Calendário"],
  ["/nova", "Nova peça"],
  ["/artes", "Artes"],
  ["/midia", "Mídia"],
  ["/contas", "Contas"],
  ["/saude", "Saúde"],
  ["/usuarios", "Usuários"]
];

// ---------------------------------------------------------------- entrada

function porta(): void {
  const app = document.getElementById("app")!;
  limpar(app);

  const email = el("input", {
    type: "email",
    id: "email",
    placeholder: "seu e-mail",
    autocomplete: "username"
  }) as HTMLInputElement;

  const senha = el("input", {
    type: "password",
    id: "senha",
    placeholder: "sua senha",
    autocomplete: "current-password"
  }) as HTMLInputElement;

  const erro = el("p", { class: "erro-linha", hidden: true });
  const botao = el("button", {}, "Entrar") as HTMLButtonElement;

  const entrar = (): void => {
    if (email.value.trim() === "" || senha.value === "") {
      erro.textContent = "Informe e-mail e senha.";
      erro.hidden = false;
      return;
    }
    botao.disabled = true;
    erro.hidden = true;

    void Api.entrar(email.value, senha.value)
      .then(() => {
        // Recarrega em vez de montar direto: garante que nada do estado da
        // tela de login sobreviva, e o cookie já vai na próxima requisição.
        location.reload();
      })
      .catch((err: unknown) => {
        botao.disabled = false;
        senha.value = "";
        erro.textContent =
          err instanceof ApiError
            ? err.message
            : "Não foi possível entrar. A API está no ar?";
        erro.hidden = false;
        senha.focus();
      });
  };

  // `form` de verdade: é o que faz o gerenciador de senhas do navegador
  // oferecer salvar, e o que faz o Enter funcionar sem código.
  const formulario = el(
    "form",
    {
      submit: (e: Event) => {
        e.preventDefault();
        entrar();
      }
    },
    email,
    senha,
    erro,
    botao
  );

  app.appendChild(
    el(
      "div",
      { class: "porta" },
      el("h1", {}, "Nex Social"),
      el("p", { class: "sub" }, "Publicador agendado."),
      formulario,
      el(
        "p",
        { class: "nota" },
        "Sem conta? Quem já tem acesso cria a sua na aba Usuários."
      )
    )
  );

  email.focus();
}

// ---------------------------------------------------------------- casca

function rotaAtual(): string {
  const h = location.hash.replace(/^#/, "");
  return ROTAS[h] ? h : "/calendario";
}

async function desenhar(): Promise<void> {
  const conteudo = document.getElementById("conteudo");
  if (!conteudo) return;

  const rota = rotaAtual();
  const tela = ROTAS[rota]!();

  for (const a of document.querySelectorAll<HTMLAnchorElement>(".menu a")) {
    a.classList.toggle("ativo", a.getAttribute("href") === `#${rota}`);
  }

  limpar(conteudo);
  conteudo.appendChild(el("h2", {}, tela.titulo));
  const corpo = el("div", { class: "corpo" });
  conteudo.appendChild(corpo);

  try {
    await tela.montar(corpo);
  } catch (err) {
    avisar(corpo, err, `Falhou ao abrir ${tela.titulo}`);
  }
}

/**
 * O seletor de marca, no topo do menu.
 *
 * Trocar de marca REDESENHA a tela aberta em vez de navegar: quem está no
 * calendário continua no calendário, agora o da outra marca. Rascunho de arte
 * fica no navegador, por marca, e não se perde na troca.
 */
function seletorDeMarca(): HTMLElement {
  const marcas = todasAsMarcas();
  if (marcas.length === 0) {
    return el("p", { class: "erro-linha" }, "nenhuma marca com conta");
  }

  const sel = el(
    "select",
    { class: "seletor-marca", "aria-label": "Marca" },
    ...marcas.map((m) => el("option", { value: m.slug }, m.nome))
  ) as HTMLSelectElement;
  sel.value = marcaAtual().slug;
  sel.addEventListener("change", () => {
    escolherMarca(sel.value);
    void desenhar();
  });
  return el(
    "label",
    { class: "marca-ativa" },
    el("span", { class: "sub" }, "marca"),
    sel
  );
}

function iniciar(usuario: UsuarioLogado | null, viaToken: boolean): void {
  const app = document.getElementById("app")!;
  limpar(app);

  const sair = el("button", { class: "sair" }, "Sair") as HTMLButtonElement;
  sair.addEventListener("click", () => {
    sair.disabled = true;
    // Recarrega mesmo se o logout falhar: o cookie pode já ter vencido, e
    // prender a pessoa numa tela por causa disso seria pior.
    void Api.sair().finally(() => location.reload());
  });

  app.appendChild(
    el(
      "div",
      { class: "layout" },
      el(
        "nav",
        { class: "menu" },
        el("div", { class: "marca" }, "Nex Social"),
        seletorDeMarca(),
        ...MENU.map(([r, nome]) => el("a", { href: `#${r}` }, nome)),
        el(
          "div",
          { class: "quem" },
          viaToken
            ? // Entrar pelo token é caminho de script. Dizer isso na tela evita
              // alguém achar que a autoria dos posts está sendo registrada
              // quando não está.
              el(
                "span",
                { class: "erro-linha" },
                "sessão por token — sem identidade"
              )
            : el("span", { class: "sub" }, usuario?.name ?? "")
        ),
        sair
      ),
      el("main", { id: "conteudo" })
    )
  );

  window.addEventListener("hashchange", () => void desenhar());
  void desenhar();
}

// ---------------------------------------------------------------- começo

void (async () => {
  try {
    const { usuario, viaToken } = await Api.quemSou();
    // As marcas ANTES de qualquer tela: todas as de conteúdo dependem da
    // escolhida, e montar uma sem ela mostraria a biblioteca de ninguém.
    definirMarcas((await Api.marcas()).brands);
    iniciar(usuario, viaToken);
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) {
      porta();
      return;
    }
    // Qualquer outra falha é da API, não da sessão. Mostrar a porta aqui faria
    // a pessoa tentar a senha várias vezes contra um servidor caído.
    const app = document.getElementById("app")!;
    limpar(app);
    const caixa = el("div", { class: "porta" });
    app.appendChild(caixa);
    avisar(caixa, err, "Não foi possível falar com a API");
  }
})();
